import React, { useState, useEffect } from "react";
import { Task } from "../types/Task";
import { Button, Col, Form, Modal, Nav, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { fetchProjects } from "../redux/actions/projectAction";
import Select from "react-select";
import { StoreState } from "../redux/reducers";
import { fetchModules } from "../redux/actions/moduleAction";
import { fetchSubModules } from "../redux/actions/subModuleAction";
import { fetchPages } from "../redux/actions/pageAction";
import { fetchSprints } from "../redux/actions/SprintAction";
import { TaskComment } from "../types/TaskComment";
import CollapseCard from "./custom/CollapseCard";
import { FaMinus, FaPlus } from "react-icons/fa";
import { addTask, editTask, fetchTasks } from "../redux/actions/taskAction";
import {
  addTaskAttachmentAction,
  deleteTaskAttachmentAction,
  fetchTaskAttachmentByIdAction,
  fetchTaskAttachmentsAction,
} from "../redux/actions/taskAttachmentAction";
import { apiUrl } from "../config";
import { ToastContainer, toast } from "react-toastify";
import {
  addTaskCommentAction,
  deleteTaskCommentAction,
  fetchTaskCommentByIdAction,
  fetchTaskCommentsAction,
} from "../redux/actions/taskCommentAction";
import TaskLogList from "./TaskLogList";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  addTaskLogAction,
  fetchTaskLogsAction,
} from "../redux/actions/taskLogActions";
import { TaskLog } from "../types/TaskLog";
import { FaFilePdf, FaFileImage, FaFileAlt } from "react-icons/fa";
import { fetchEmployeesAction } from "../redux/actions/employeeActions";
import RichTextEditor from "./RichTextEditor";

interface Props {
  onSave: (task: Task) => void;
  initialData?: Task | null;
  initialDataComment?: TaskComment | null;
  clearAfterSubmit: () => void;
  readOnly?: boolean;
  attachmentUrl?: string;
}

const initialState: Task = {
  id: 0,
  pageId: 0,
  projectId: 0,
  type: "",
  title: "",
  description: "",
  priority: "",
  size: "",
  status: "New",
  assignedDev: "",
  assignedQa: "",
  assignedBa: "",
  devEstHrs: 0,
  devActHrs: 0,
  qaEstHrs: 0,
  qaActHrs: 0,
  baEstHrs: 0,
  baActHrs: 0,
  parentTaskId: null,
  childId: null,
  startDate: "",
  completionDate: null,
  sprintId: 0,
  planReleaseVersion: "",
  openCount: 0,
  createBy: "",
  moduleId: 0,
  subModuleId: 0,
};

const TaskForm: React.FC<Props> = ({
  onSave,
  initialData,
  clearAfterSubmit,
  readOnly = false,
  initialDataComment,
}) => {
  const [task, setTask] = useState<Task>(initialData || initialState);
  const tasks = useSelector((state: StoreState) => state.task);
  const [linkedParentId, setLinkedParentId] = useState<number | null>(null);
  const [showSubTaskForm, setShowSubTaskForm] = useState(false);
  const [subTask, setSubTask] = useState<Task>({ ...initialState });
  const dispatch = useDispatch<any>();
  const [project, setProject] = useState<{ id: number; name: string }>({
    id: 0,
    name: "",
  });
  const [module, setModule] = useState<{ id: number; name: string }>({
    id: 0,
    name: "",
  });
  const [subModule, setSubModule] = useState<{ id: number; name: string }>({
    id: 0,
    name: "",
  });
  const [page, setPage] = useState<{ id: number; name: string }>({
    id: 0,
    name: "",
  });
  const [sprint, setSprint] = useState<{ id: number; name: string }>({
    id: 0,
    name: "",
  });
  const projects = useSelector((state: StoreState) => state.project);
  const employees = useSelector((state: StoreState) => state.employee);
  const modules = useSelector((state: StoreState) => state.module);
  const subModules = useSelector((state: StoreState) => state.subModule);
  const pages = useSelector((state: StoreState) => state.page);
  const sprints = useSelector((state: StoreState) => state.sprint);
  const [status, setStatus] = useState(initialData?.status || "New");
  const [isEditable, setIsEditable] = useState<boolean>(!readOnly);
  const [activeTab, setActiveTab] = useState("details");
  const authUser = useSelector((state: StoreState) => state.authUser);
  useEffect(() => {
    if (initialData) {
      setStatus(initialData.status);
    }
  }, [initialData]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const updatedTask = {
      ...task,
      assignedDev: task.assignedDev === "0" ? "" : task.assignedDev,
      assignedQa: task.assignedQa === "0" ? "" : task.assignedQa,
      assignedBa: task.assignedBa === "0" ? "" : task.assignedBa,
      status: status,
    };
    onSave(updatedTask);
    if (!task.id) {
      setTask(initialState);
      clearAfterSubmit();
    }
  };
  const getFileIcon = (file: string | File | null | undefined) => {
    if (!file) return null;

    let fileType = "";

    if (typeof file === "string") {
      // Determine type from URL or extension
      const extension = file.split(".").pop()?.toLowerCase();
      if (extension === "pdf")
        return <FaFilePdf className="text-danger ms-2" />;
      if (
        ["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(extension || "")
      )
        return <FaFileImage className="text-primary ms-2" />;
      return <FaFileAlt className="text-muted ms-2" />;
    }

    if (file instanceof File) {
      fileType = file.type;
    }

    if (fileType === "application/pdf")
      return <FaFilePdf className="text-danger ms-2" />;
    if (fileType.startsWith("image/"))
      return <FaFileImage className="text-primary ms-2" />;
    return <FaFileAlt className="text-muted ms-2" />;
  };
  useEffect(() => {
    if (initialData) {
      setTask(initialData);
    }
    setIsEditable(!readOnly);
  }, [initialData, readOnly]);
  useEffect(() => {
    dispatch(fetchProjects());
    dispatch(fetchModules());
    dispatch(fetchSubModules());
    dispatch(fetchPages());
    dispatch(fetchSprints());
  }, [dispatch]);
  useEffect(() => {
    if (initialData) {
      setTask(initialData);
      const proj = projects.find((p) => p.id === initialData.projectId);
      const mod = modules.find((p) => p.id === initialData.moduleId);
      const subMod = subModules.find((p) => p.id === initialData.subModuleId);
      const pg = pages.find((p) => p.id === initialData.pageId);
      const spr = sprints.find((p) => p.id === initialData.sprintId);

      if (proj) setProject({ id: proj.id, name: proj.name });
      if (mod) setModule({ id: mod.id, name: mod.name });
      if (subMod) setSubModule({ id: subMod.id, name: subMod.name });
      if (pg) setPage({ id: pg.id, name: pg.name });
      if (spr) setSprint({ id: spr.id, name: spr.sprint });
    }
  }, [initialData, projects, modules, subModules, pages, sprints, employees]);

  const handleChange = (e: any) => {
    const { name, value } = e.target || e;
    if (/^\d*$/.test(value)) {
      let cleanValue = value.replace(/^0+/, "");
      setTask((prev) => ({
        ...prev,
        [name]: cleanValue === "" ? "" : cleanValue,
      }));
    }
  };
  const handleClear = () => {
    setTask(initialState);
  };
  const taskId = initialData?.id?.toString();
  const attachments = useSelector(
    (state: StoreState) => state.taskAttachment || [],
  );
  type AttachmentField = {
    attachmentTitle: string;
    attachmentUrl: File | null;
  };
  const [attachmentFields, setAttachmentFields] = useState<AttachmentField[]>([
    { attachmentTitle: "", attachmentUrl: null },
  ]);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [selectedId, setSelectedId] = useState<number>(0);
  const attachment = attachments.filter(
    (task) => String(task.taskId) === taskId,
  );
  useEffect(() => {
    if (taskId) {
      dispatch(fetchTasks());
      dispatch(fetchTaskAttachmentsAction());
    }
  }, [dispatch, taskId]);
  const handleAttachmentNameChange = (index: number, value: string) => {
    setAttachmentFields((prev) => {
      const updatedFields = [...prev];
      updatedFields[index].attachmentTitle = value;
      return updatedFields;
    });
  };
  const handleFileChange = (
    index: number,
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      setAttachmentFields((prev) => {
        const updatedFields = [...prev];
        updatedFields[index].attachmentUrl = file;
        return updatedFields;
      });
    }
  };
  const addAttachmentField = () => {
    setAttachmentFields([
      ...attachmentFields,
      { attachmentTitle: "", attachmentUrl: null },
    ]);
  };
  const removeAttachmentField = (index: number) => {
    setAttachmentFields(attachmentFields.filter((_, i) => i !== index));
  };
  const handleUpload = async () => {
    if (!taskId) return;
    for (const field of attachmentFields) {
      if (!field.attachmentTitle || !field.attachmentUrl) {
        toast.error("Please fill all fields and select a file.");
        return;
      }
      const formData = new FormData();
      formData.append("taskId", taskId);
      formData.append("attachmentTitle", field.attachmentTitle);
      formData.append("attachmentUrl", field.attachmentUrl);
      try {
        await dispatch(addTaskAttachmentAction(formData));
        toast.success("File uploaded successfully");
      } catch (err) {
        toast.error(String(err));
      }
    }
    setAttachmentFields([{ attachmentTitle: "", attachmentUrl: null }]);
  };
  const handleView = (url: string) => {
    if (url) {
      window.open(`${apiUrl}/${url}`, "_blank");
    } else {
      toast.error("Invalid URL for the attachment.");
    }
  };
  const openDeleteConfirmation = (id: number) => {
    setSelectedId(id);
    setShowDeleteConfirmation(true);
  };
  const confirmDeleteHandler = async () => {
    try {
      await dispatch(deleteTaskAttachmentAction(selectedId));
      toast.success("Attachment deleted successfully");
      setShowDeleteConfirmation(false);
      if (taskId) {
        dispatch(fetchTaskAttachmentByIdAction(taskId));
      }
    } catch (err) {
      toast.error("Failed to delete attachment");
    }
  };
  const comments = useSelector((state: StoreState) => state.taskComment || []);
  const taskLogs = useSelector((state: StoreState) => state.taskLog || []);
  type CommentField = {
    message: string;
    createBy: string;
  };
  const [commentFields, setCommentFields] = useState<CommentField[]>([
    { message: "", createBy: authUser.name },
  ]);
  type TaskLogField = {
    message: string;
    createBy: string;
  };
  const [taskLogFields, setTaskLogFields] = useState<TaskLogField[]>([
    { message: "", createBy: authUser.name },
  ]);
  const comment = comments.filter((task) => String(task.taskId) === taskId);
  const taskLog = taskLogs.filter((task) => String(task.taskId) === taskId);
  useEffect(() => {
    if (taskId) {
      dispatch(fetchTasks());
      dispatch(fetchTaskCommentsAction());
      dispatch(fetchTaskLogsAction());
    }
  }, [dispatch, taskId]);
  const handleCommentChange = (index: number, value: string) => {
    setCommentFields((prev) => {
      const updatedFields = [...prev];
      updatedFields[index].message = value;
      return updatedFields;
    });
  };
  const handleTaskLogChange = (index: number, value: string) => {
    setTaskLogFields((prev) => {
      const updatedFields = [...prev];
      updatedFields[index].message = value;
      return updatedFields;
    });
  };
  const removeCommentField = (index: number) => {
    setCommentFields(commentFields.filter((_, i) => i !== index));
  };
  const openDeleteConfirmationComment = (id: number) => {
    setSelectedId(id);
    setShowDeleteConfirmation(true);
  };
  const confirmCommentDeleteHandler = async () => {
    try {
      await dispatch(deleteTaskCommentAction(selectedId));
      toast.success("Comment deleted successfully");
      setShowDeleteConfirmation(false);
      if (taskId) {
        dispatch(fetchTaskCommentByIdAction(taskId));
      }
    } catch (err) {
      toast.error("Failed to delete comment");
    }
  };
  const preventScroll = (e: React.WheelEvent<HTMLInputElement>) => {
    e.currentTarget.blur();
  };
  const hasSubTasks =
    task.id && tasks.some((t) => Number(t.parentTaskId) === Number(task.id));

  const renderFormContent = () => {
    if (activeTab === "attachment") {
      return (
        <>
          <CollapseCard title="Upload Attachment">
            <div>
              {attachmentFields.map((field, index) => (
                <div className="row" key={index}>
                  <div className="col-md-4">
                    <label>Attachment Name *</label>
                    <div className="d-flex align-items-center">
                      <input
                        type="text"
                        className="form-control"
                        value={field.attachmentTitle}
                        onChange={(e) =>
                          handleAttachmentNameChange(index, e.target.value)
                        }
                      />
                      {getFileIcon(field.attachmentUrl)}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <label>PDF File *</label>
                    <input
                      type="file"
                      className="form-control"
                      // accept="application/pdf"
                      onChange={(e) => handleFileChange(index, e)}
                    />
                  </div>
                  <div className="col-md-2 mt-4">
                    {index > 0 && (
                      <Button
                        variant="danger"
                        onClick={() => removeAttachmentField(index)}
                      >
                        <FaMinus />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="m-4 d-flex justify-content-end">
              <Button
                className="m-1"
                variant="success"
                onClick={addAttachmentField}
              >
                <FaPlus /> Add Another Attachment
              </Button>
              <Button
                className="m-1"
                onClick={handleUpload}
                style={{ backgroundColor: "#10296C", color: "#fff" }}
              >
                Upload
              </Button>
            </div>
          </CollapseCard>
          <CollapseCard title="Attachments List">
            <div className="container-fluid">
              <table className="table table-striped">
                <thead style={{ backgroundColor: "#10296C", color: "#fff" }}>
                  <tr>
                    <th>S.No</th>
                    <th>Attachment Name</th>
                    <th>File</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {attachment.map((attachment, index) => (
                    <tr key={attachment.id}>
                      <td>{index + 1}</td>
                      <td className="d-flex align-items-center">
                        <span> {getFileIcon(attachment.attachmentUrl)}</span>
                        <span> {attachment.attachmentTitle}</span>
                      </td>
                      <td>
                        <Button
                          className="btn-sm"
                          onClick={() =>
                            typeof attachment.attachmentUrl === "string"
                              ? handleView(attachment.attachmentUrl)
                              : toast.error("Invalid URL for the attachment.")
                          }
                          style={{ backgroundColor: "#10296C", color: "#fff" }}
                        >
                          View
                        </Button>
                      </td>
                      <td>
                        <Button
                          className="btn btn-danger btn-sm"
                          onClick={() => openDeleteConfirmation(attachment.id)}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CollapseCard>
          <ToastContainer />
          <>
            <Modal
              show={showDeleteConfirmation}
              onHide={() => setShowDeleteConfirmation(false)}
            >
              <Modal.Header closeButton>
                <Modal.Title>Confirm Deletion</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <>Are you sure you want to delete this attachment?</>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="secondary"
                  onClick={() => setShowDeleteConfirmation(false)}
                >
                  Cancel
                </Button>
                <Button variant="danger" onClick={confirmDeleteHandler}>
                  Delete
                </Button>
              </Modal.Footer>
            </Modal>
          </>
        </>
      );
    } else if (activeTab === "comment") {
      return (
        <>
          <div
            style={{ maxHeight: "300px", overflowY: "auto", padding: "1rem" }}
          >
            {[...comment].reverse().map((c, index) => (
              <div key={c.id} className="mb-3">
                <div className="d-flex align-items-start">
                  <span style={{ marginRight: "8px", color: "#10296C" }}>
                    ➤
                  </span>
                  <div style={{ wordBreak: "break-word", flex: 1 }}>
                    {c.message}
                  </div>
                </div>
                <div
                  className="d-flex justify-content-end"
                  style={{ color: "grey", fontSize: "0.85rem" }}
                >
                  {c.createBy} |{" "}
                  {new Date(c.createTimestamp).toLocaleString("en-IN", {
                    timeZone: "Asia/Kolkata",
                  })}
                </div>

                <hr />
              </div>
            ))}
          </div>

          <div className="container mt-3">
            <div className="row">
              <div className="col-md-10">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter comment..."
                  value={commentFields[0]?.message}
                  onChange={(e) => handleCommentChange(0, e.target.value)}
                />
              </div>
              <div className="col-md-2 d-flex align-items-center">
                <Button
                  className="w-100"
                  style={{ backgroundColor: "#10296C", color: "#fff" }}
                  onClick={async () => {
                    if (!taskId) return;
                    const message = commentFields[0]?.message;
                    const createBy = commentFields[0]?.createBy;
                    if (!message) {
                      toast.error("Please fill the message");
                      return;
                    }

                    const data: TaskComment = {
                      id: 0,
                      taskId: Number(taskId),
                      message,
                      createBy: createBy,
                      createTimestamp: new Date().toLocaleString("en-US", {
                        timeZone: "Asia/Kolkata",
                      }),
                    };

                    try {
                      await dispatch(addTaskCommentAction(data));
                      toast.success("Comment added successfully");
                      dispatch(fetchTaskCommentsAction());
                      setCommentFields([
                        { message: "", createBy: authUser.name },
                      ]);
                    } catch (err) {
                      toast.error("Failed to add comment");
                    }
                  }}
                >
                  Submit
                </Button>
              </div>
            </div>
          </div>

          <ToastContainer />
        </>
      );
    } else if (activeTab === "history") {
      return (
        <>
          <div
            style={{ maxHeight: "300px", overflowY: "auto", padding: "1rem" }}
          >
            {[...taskLog].reverse().map((c, index) => (
              <div key={c.id} className="mb-3">
                <div className="d-flex align-items-start">
                  <span style={{ marginRight: "8px", color: "#10296C" }}>
                    ➤
                  </span>
                  <div style={{ wordBreak: "break-word", flex: 1 }}>
                    {c.message}
                  </div>
                </div>
                <div
                  className="d-flex justify-content-end"
                  style={{ color: "grey", fontSize: "0.85rem" }}
                >
                  {authUser.name} |{" "}
                  {new Date(c?.createTimestamp ?? "").toLocaleString("en-IN", {
                    timeZone: "Asia/Kolkata",
                  })}
                </div>

                <hr />
              </div>
            ))}
          </div>
          <ToastContainer />
        </>
      );
    } else if (activeTab === "linked") {
      if (showSubTaskForm) {
        return (
          <>
            <h5>Add Sub Task</h5>
            <Row className="mb-1">
              <Col className="form-column">
                <Form.Group>
                  <Form.Label>
                    Project
                    {/* <span className="text-danger">*</span> */}
                  </Form.Label>
                  <Select
                    required
                    isDisabled={!readOnly}
                    options={projects.map((p) => ({
                      label: p.name,
                      value: p.id.toString(),
                    }))}
                    value={
                      project.id
                        ? {
                            label:
                              projects.find((p) => p.id === project.id)?.name ||
                              "",
                            value: project.id.toString(),
                          }
                        : null
                    }
                    placeholder="Select Project"
                    isMulti={false}
                    onChange={(selectedOption) => {
                      if (selectedOption) {
                        const id = parseInt(selectedOption.value, 10);
                        const name =
                          projects.find((p) => p.id === id)?.name || "";
                        setProject({ id, name });
                        setTask((prev) => ({ ...prev, projectId: id }));
                      } else {
                        setProject({ id: 0, name: "" });
                        setTask((prev) => ({ ...prev, projectId: 0 }));
                      }
                    }}
                  />
                </Form.Group>
              </Col>
              <Col className="form-column">
                <Form.Group>
                  <Form.Label>Module</Form.Label>
                  <Select
                    isDisabled={!readOnly}
                    options={modules
                      .filter((m) => m.projectId === project.id)
                      .map((p) => ({
                        label: p.name,
                        value: p.id.toString(),
                      }))}
                    value={
                      module.id
                        ? {
                            label:
                              modules.find((p) => p.id === module.id)?.name ||
                              "",
                            value: module.id.toString(),
                          }
                        : null
                    }
                    placeholder="Select Module"
                    isMulti={false}
                    onChange={(selectedOption) => {
                      if (selectedOption) {
                        const id = parseInt(selectedOption.value, 10);
                        const name =
                          modules.find((p) => p.id === id)?.name || "";
                        setModule({ id, name });
                        setSubModule({ id: 0, name: "" });
                        setPage({ id: 0, name: "" });
                        setTask((prev) => ({
                          ...prev,
                          moduleId: id,
                          subModuleId: 0,
                          pageId: 0,
                        }));
                      } else {
                        setModule({ id: 0, name: "" });
                        setTask((prev) => ({ ...prev, moduleId: 0 }));
                      }
                    }}
                  />
                </Form.Group>
              </Col>
              <Col className="form-column">
                <Form.Group>
                  <Form.Label>Sub - Module</Form.Label>
                  <Select
                    isDisabled={!readOnly}
                    options={subModules
                      .filter((sm) => sm.moduleId === module.id)
                      .map((p) => ({
                        label: p.name,
                        value: p.id.toString(),
                      }))}
                    value={
                      subModule.id
                        ? {
                            label:
                              subModules.find((p) => p.id === subModule.id)
                                ?.name || "",
                            value: subModule.id.toString(),
                          }
                        : null
                    }
                    placeholder="Select Submodule"
                    isMulti={false}
                    onChange={(selectedOption) => {
                      if (selectedOption) {
                        const id = parseInt(selectedOption.value, 10);
                        const name =
                          subModules.find((p) => p.id === id)?.name || "";
                        setSubModule({ id, name });
                        setPage({ id: 0, name: "" });
                        setTask((prev) => ({
                          ...prev,
                          subModuleId: id,
                          pageId: 0,
                        }));
                      } else {
                        setSubModule({ id: 0, name: "" });
                        setTask((prev) => ({ ...prev, subModuleId: 0 }));
                      }
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-1">
              <Col className="form-column">
                <Form.Group>
                  <Form.Label>Page</Form.Label>
                  <Select
                    isDisabled={!readOnly}
                    options={pages
                      .filter((p) => p.subModuleId === subModule.id)
                      .map((p) => ({
                        label: p.name,
                        value: p.id.toString(),
                      }))}
                    value={
                      page.id
                        ? {
                            label:
                              pages.find((p) => p.id === page.id)?.name || "",
                            value: page.id.toString(),
                          }
                        : null
                    }
                    placeholder="Select Page"
                    isMulti={false}
                    onChange={(selectedOption) => {
                      if (selectedOption) {
                        const id = parseInt(selectedOption.value, 10);
                        const name = pages.find((p) => p.id === id)?.name || "";
                        setPage({ id, name });
                        setTask((prev) => ({ ...prev, pageId: id }));
                      } else {
                        setPage({ id: 0, name: "" });
                        setTask((prev) => ({ ...prev, pageId: 0 }));
                      }
                    }}
                  />
                </Form.Group>
              </Col>
              <Col className="form-column">
                <Form.Group>
                  <Form.Label>Sprint</Form.Label>
                  <Select
                    isDisabled={readOnly}
                    options={sprints.map((p) => ({
                      label: p.sprint,
                      value: p.id.toString(),
                    }))}
                    value={
                      sprint.id
                        ? {
                            label:
                              sprints.find((p) => p.id === sprint.id)?.sprint ||
                              "",
                            value: sprint.id.toString(),
                          }
                        : null
                    }
                    placeholder="Select Sprint"
                    isMulti={false}
                    onChange={(selectedOption) => {
                      if (selectedOption) {
                        const id = parseInt(selectedOption.value, 10);
                        const name =
                          sprints.find((p) => p.id === id)?.sprint || "";
                        setSprint({ id, name });
                        setTask((prev) => ({ ...prev, sprintId: id }));
                      } else {
                        setSprint({ id: 0, name: "" });
                        setTask((prev) => ({ ...prev, sprintId: 0 }));
                      }
                    }}
                  />
                </Form.Group>
              </Col>
              <Col className="form-column">
                <Form.Group>
                  <Form.Label>Priority</Form.Label>
                  <Form.Select
                    name="priority"
                    value={task.priority}
                    onChange={handleChange}
                    disabled={readOnly}
                    style={{
                      borderColor: "rgb(215, 210, 210)",
                    }}
                  >
                    <option value="">Select Priority</option>
                    <option value="High">High</option>
                    <option value="Medium">Medium</option>
                    <option value="Low">Low</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-1">
              <Col className="form-column1">
                <Form.Group>
                  <Form.Label>Title</Form.Label>
                  <Form.Control
                    name="title"
                    value={subTask.title}
                    onChange={(e) =>
                      setSubTask((prev) => ({ ...prev, title: e.target.value }))
                    }
                    placeholder="Enter title"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-1">
              <Col className="form-column1">
                <Form.Group>
                  <Form.Label>Description</Form.Label>
                  <RichTextEditor
                    value={subTask.description}
                    initialValue={subTask.description}
                    isEditable={true}
                    onChange={(html) =>
                      setSubTask((prev) => ({ ...prev, description: html }))
                    }
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-1">
              <Col className="form-column">
                <Form.Group>
                  <Form.Label>Dev Est. Hours</Form.Label>
                  <Form.Control
                    type="text"
                    name="devEstHrs"
                    value={subTask.devEstHrs.toString()}
                    onChange={(e) => {
                      let value = e.target.value;
                      if (!/^\d*$/.test(value)) return;
                      if (value.length > 1 && value.startsWith("0")) {
                        value = value.replace(/^0+/, "");
                      }

                      setSubTask((prev) => ({
                        ...prev,
                        devEstHrs: Number(value || 0),
                      }));
                    }}
                    inputMode="numeric"
                  />
                </Form.Group>
              </Col>
              <Col className="form-column">
                <Form.Group>
                  <Form.Label>Dev Act. Hours</Form.Label>
                  <Form.Control
                    type="text"
                    name="devActHrs"
                    value={subTask.devActHrs.toString()}
                    onChange={(e) => {
                      const { name, value } = e.target;
                      if (value !== "" && !/^\d*$/.test(value)) return;
                      const cleanValue =
                        value === "" ? "0" : value.replace(/^0+/, "") || "0";

                      setSubTask((prev) => ({
                        ...prev,
                        [name]: Number(cleanValue),
                      }));
                    }}
                    inputMode="numeric"
                    onWheel={preventScroll}
                  />
                </Form.Group>
              </Col>
              <Col className="form-column">
                <Form.Group>
                  <Form.Label>QA Est. Hours</Form.Label>
                  <Form.Control
                    type="text"
                    name="qaEstHrs"
                    value={subTask.qaEstHrs.toString()}
                    onChange={(e) => {
                      const { name, value } = e.target;
                      if (value !== "" && !/^\d*$/.test(value)) return;
                      const cleanValue =
                        value === "" ? "0" : value.replace(/^0+/, "") || "0";

                      setSubTask((prev) => ({
                        ...prev,
                        [name]: Number(cleanValue),
                      }));
                    }}
                    inputMode="numeric"
                    onWheel={preventScroll}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-1">
              <Col className="form-column">
                <Form.Group>
                  <Form.Label>QA Act. Hours</Form.Label>
                  <Form.Control
                    type="text"
                    name="qaActHrs"
                    value={subTask.qaActHrs}
                    onChange={(e) => {
                      const { name, value } = e.target;

                      if (!/^\d*$/.test(value)) return;
                      const cleanValue = value.replace(/^0+/, "");

                      setSubTask((prev) => ({
                        ...prev,
                        [name]: cleanValue,
                      }));
                    }}
                  />
                </Form.Group>
              </Col>
              <Col className="form-column">
                <Form.Group>
                  <Form.Label>BA Est. Hours</Form.Label>
                  <Form.Control
                    type="text"
                    name="baEstHrs"
                    value={subTask.baEstHrs}
                    onChange={(e) => {
                      const { name, value } = e.target;

                      if (!/^\d*$/.test(value)) return;
                      const cleanValue = value.replace(/^0+/, "");

                      setSubTask((prev) => ({
                        ...prev,
                        [name]: cleanValue,
                      }));
                    }}
                    onWheel={preventScroll}
                    inputMode="numeric"
                  />
                </Form.Group>
              </Col>
              <Col className="form-column">
                <Form.Group>
                  <Form.Label>BA Act. Hours</Form.Label>
                  <Form.Control
                    type="text"
                    name="baActHrs"
                    value={subTask.baActHrs}
                    onChange={(e) => {
                      const { name, value } = e.target;

                      if (!/^\d*$/.test(value)) return;
                      const cleanValue = value.replace(/^0+/, "");

                      setSubTask((prev) => ({
                        ...prev,
                        [name]: cleanValue,
                      }));
                    }}
                    onWheel={preventScroll}
                    inputMode="numeric"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-1">
              <Col className="form-column">
                <Form.Group>
                  <Form.Label>Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="startDate"
                    value={subTask.startDate}
                    onChange={(e) =>
                      setSubTask((prev) => ({
                        ...prev,
                        [e.target.name]: e.target.value,
                      }))
                    }
                  />
                </Form.Group>
              </Col>
              <Col className="form-column">
                <Form.Group>
                  <Form.Label>Completion Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="completionDate"
                    value={subTask.completionDate || ""}
                    onChange={(e) =>
                      setSubTask((prev) => ({
                        ...prev,
                        [e.target.name]: e.target.value,
                      }))
                    }
                  />
                </Form.Group>
              </Col>
              <Col className="form-column">
                <Form.Group>
                  <Form.Label>Plan Release Version</Form.Label>
                  <Form.Control
                    name="planReleaseVersion"
                    value={subTask.planReleaseVersion}
                    onChange={(e) =>
                      setSubTask((prev) => ({
                        ...prev,
                        [e.target.name]: e.target.value,
                      }))
                    }
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-1">
              <Col className="form-column">
                <Form.Group>
                  <Form.Label>Assigned Developer</Form.Label>
                  <Select
                    required
                    isDisabled={readOnly}
                    options={employees.map((p) => ({
                      label: p.name,
                      value: p.id.toString(),
                    }))}
                    value={
                      subTask.assignedDev && subTask.assignedDev !== "0"
                        ? {
                            label: subTask.assignedDev,
                            value:
                              employees
                                .find((p) => p.name === subTask.assignedDev)
                                ?.id.toString() || "",
                          }
                        : null
                    }
                    placeholder="Select employee"
                    isMulti={false}
                    onChange={(selectedOption) => {
                      if (selectedOption) {
                        const id = parseInt(selectedOption.value, 10);
                        const name =
                          employees.find((p) => p.id === id)?.name || "";
                        setSubTask((prev) => ({ ...prev, assignedDev: name }));
                      } else {
                        setSubTask((prev) => ({ ...prev, assignedDev: "" }));
                      }
                    }}
                  />
                </Form.Group>
              </Col>
              <Col className="form-column">
                <Form.Group>
                  <Form.Label>Assigned QA</Form.Label>
                  <Select
                    required
                    isDisabled={readOnly}
                    options={employees.map((p) => ({
                      label: p.name,
                      value: p.id.toString(),
                    }))}
                    value={
                      subTask.assignedQa && subTask.assignedQa !== "0"
                        ? {
                            label: subTask.assignedQa,
                            value:
                              employees
                                .find((p) => p.name === subTask.assignedQa)
                                ?.id.toString() || "",
                          }
                        : null
                    }
                    placeholder="Select employee"
                    isMulti={false}
                    onChange={(selectedOption) => {
                      if (selectedOption) {
                        const id = parseInt(selectedOption.value, 10);
                        const name =
                          employees.find((p) => p.id === id)?.name || "";
                        setSubTask((prev) => ({ ...prev, assignedQa: name }));
                      } else {
                        setSubTask((prev) => ({ ...prev, assignedQa: "" }));
                      }
                    }}
                  />
                </Form.Group>
              </Col>
              <Col className="form-column">
                <Form.Group>
                  <Form.Label>Assigned BA</Form.Label>
                  <Select
                    required
                    isDisabled={readOnly}
                    options={employees.map((p) => ({
                      label: p.name,
                      value: p.id.toString(),
                    }))}
                    value={
                      subTask.assignedBa && subTask.assignedBa !== "0"
                        ? {
                            label: subTask.assignedBa,
                            value:
                              employees
                                .find((p) => p.name === subTask.assignedBa)
                                ?.id.toString() || "",
                          }
                        : null
                    }
                    placeholder="Select employee"
                    isMulti={false}
                    onChange={(selectedOption) => {
                      if (selectedOption) {
                        const id = parseInt(selectedOption.value, 10);
                        const name =
                          employees.find((p) => p.id === id)?.name || "";
                        setSubTask((prev) => ({ ...prev, assignedBa: name }));
                      } else {
                        setSubTask((prev) => ({ ...prev, assignedBa: "" }));
                      }
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-1">
              {task.parentTaskId && (
                <>
                  <Button
                    className="btn-add"
                    onClick={() => {
                      const parent = tasks.find(
                        (t) => t.id === task.parentTaskId,
                      );
                      if (parent) {
                        setTask(parent);
                        setActiveTab("details");
                        setIsEditable(true);
                      } else {
                        toast.error("Parent task not found");
                      }
                    }}
                  >
                    View Parent Task
                  </Button>
                </>
              )}
            </Row>
            <Row className="mb-1">
              <Col md={12}>
                <div className="form-buttons">
                  <Button
                    variant="primary"
                    onClick={async () => {
                      if (!subTask.title || !subTask.description) {
                        toast.error("Fill required fields");
                        return;
                      }

                      await dispatch(addTask(subTask));
                      toast.success("Sub-task added");
                      setShowSubTaskForm(false);
                    }}
                  >
                    Add Sub Task
                  </Button>

                  <Button
                    variant="secondary"
                    onClick={() => {
                      setShowSubTaskForm(false);
                      setActiveTab("details");
                    }}
                  >
                    Go to Parent Task
                  </Button>
                </div>
              </Col>
            </Row>
          </>
        );
      }

      const parentId = task.parentTaskId || task.id;
      const linkedSubTasks = tasks.filter(
        (t) => Number(t.parentTaskId) === Number(parentId),
      );

      return (
        <>
          {linkedSubTasks.length === 0 ? (
            <p style={{ color: "gray" }}>No linked sub-tasks found.</p>
          ) : (
            <>
              <h5>Linked Sub Tasks</h5>
              {linkedSubTasks.map((sub) => (
                <div key={sub.id} className="hstack gap-2 mb-2">
                  <span style={{ fontWeight: "bold" }}>
                    <li>{sub.title}</li>
                  </span>
                  <Button
                    className="btn-add m-2"
                    onClick={() => {
                      setTask(sub);
                      setActiveTab("details");
                      setIsEditable(true);
                      setShowSubTaskForm(false);
                    }}
                  >
                    View
                  </Button>
                </div>
              ))}
            </>
          )}
        </>
      );
    }
    return null;
  };
  useEffect(() => {
    dispatch(fetchEmployeesAction());
  }, []);

  return (
    <div className="d-flex">
      <div
        className="left-nav"
        style={{ width: "200px", borderRight: "1px solid #dee2e6" }}
      >
        <Nav className="flex-column">
          <Nav.Link
            className={`nav-item ${activeTab === "details" ? "active" : ""}`}
            onClick={() => setActiveTab("details")}
          >
            Details
          </Nav.Link>

          <Nav.Link
            className={`nav-item ${activeTab === "attachment" ? "active" : ""}`}
            onClick={() => setActiveTab("attachment")}
          >
            Attachments
          </Nav.Link>
          <Nav.Link
            className={`nav-item ${activeTab === "comment" ? "active" : ""}`}
            onClick={() => setActiveTab("comment")}
          >
            Comments
          </Nav.Link>
          <Nav.Link
            className={`nav-item ${activeTab === "linked" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("linked");
              setShowSubTaskForm(false);

              const firstSubTask = tasks.find(
                (t) => t.parentTaskId === task.id,
              );
              if (firstSubTask) {
                setTask(firstSubTask);
                setIsEditable(true);
              }
            }}
          >
            Linked Entities
          </Nav.Link>
          <Nav.Link
            className={`nav-item ${activeTab === "history" ? "active" : ""}`}
            onClick={() => setActiveTab("history")}
          >
            History
          </Nav.Link>
        </Nav>
      </div>
      <div className="right-form" style={{ flex: 1, padding: "20px" }}>
        <Form onSubmit={handleSubmit}>
          {activeTab === "details" && (
            <>
              <Row className="mb-1">
                <Col className="form-column">
                  <Form.Group>
                    <Form.Label>
                      Project
                      {/* <span className="text-danger">*</span> */}
                    </Form.Label>
                    <Select
                      required
                      isDisabled={readOnly}
                      options={projects.map((p) => ({
                        label: p.name,
                        value: p.id.toString(),
                      }))}
                      value={
                        project.id
                          ? {
                              label:
                                projects.find((p) => p.id === project.id)
                                  ?.name || "",
                              value: project.id.toString(),
                            }
                          : null
                      }
                      placeholder="Select Project"
                      isMulti={false}
                      onChange={(selectedOption) => {
                        if (selectedOption) {
                          const id = parseInt(selectedOption.value, 10);
                          const name =
                            projects.find((p) => p.id === id)?.name || "";
                          setProject({ id, name });
                          setTask((prev) => ({ ...prev, projectId: id }));
                        } else {
                          setProject({ id: 0, name: "" });
                          setTask((prev) => ({ ...prev, projectId: 0 }));
                        }
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col className="form-column">
                  <Form.Group>
                    <Form.Label>Module</Form.Label>
                    <Select
                      isDisabled={readOnly || !project.id}
                      options={modules
                        .filter((m) => m.projectId === project.id)
                        .map((p) => ({
                          label: p.name,
                          value: p.id.toString(),
                        }))}
                      value={
                        module.id
                          ? {
                              label:
                                modules.find((p) => p.id === module.id)?.name ||
                                "",
                              value: module.id.toString(),
                            }
                          : null
                      }
                      placeholder="Select Module"
                      isMulti={false}
                      onChange={(selectedOption) => {
                        if (selectedOption) {
                          const id = parseInt(selectedOption.value, 10);
                          const name =
                            modules.find((p) => p.id === id)?.name || "";
                          setModule({ id, name });
                          setSubModule({ id: 0, name: "" });
                          setPage({ id: 0, name: "" });
                          setTask((prev) => ({
                            ...prev,
                            moduleId: id,
                            subModuleId: 0,
                            pageId: 0,
                          }));
                        } else {
                          setModule({ id: 0, name: "" });
                          setTask((prev) => ({ ...prev, moduleId: 0 }));
                        }
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col className="form-column">
                  <Form.Group>
                    <Form.Label>Sub - Module</Form.Label>
                    <Select
                      isDisabled={readOnly || !module.id}
                      options={subModules
                        .filter((sm) => sm.moduleId === module.id)
                        .map((p) => ({
                          label: p.name,
                          value: p.id.toString(),
                        }))}
                      value={
                        subModule.id
                          ? {
                              label:
                                subModules.find((p) => p.id === subModule.id)
                                  ?.name || "",
                              value: subModule.id.toString(),
                            }
                          : null
                      }
                      placeholder="Select Submodule"
                      isMulti={false}
                      onChange={(selectedOption) => {
                        if (selectedOption) {
                          const id = parseInt(selectedOption.value, 10);
                          const name =
                            subModules.find((p) => p.id === id)?.name || "";
                          setSubModule({ id, name });
                          setPage({ id: 0, name: "" });
                          setTask((prev) => ({
                            ...prev,
                            subModuleId: id,
                            pageId: 0,
                          }));
                        } else {
                          setSubModule({ id: 0, name: "" });
                          setTask((prev) => ({ ...prev, subModuleId: 0 }));
                        }
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-1">
                <Col className="form-column">
                  <Form.Group>
                    <Form.Label>Page</Form.Label>
                    <Select
                      isDisabled={readOnly || !subModule.id}
                      options={pages
                        .filter((p) => p.subModuleId === subModule.id)
                        .map((p) => ({
                          label: p.name,
                          value: p.id.toString(),
                        }))}
                      value={
                        page.id
                          ? {
                              label:
                                pages.find((p) => p.id === page.id)?.name || "",
                              value: page.id.toString(),
                            }
                          : null
                      }
                      placeholder="Select Page"
                      isMulti={false}
                      onChange={(selectedOption) => {
                        if (selectedOption) {
                          const id = parseInt(selectedOption.value, 10);
                          const name =
                            pages.find((p) => p.id === id)?.name || "";
                          setPage({ id, name });
                          setTask((prev) => ({ ...prev, pageId: id }));
                        } else {
                          setPage({ id: 0, name: "" });
                          setTask((prev) => ({ ...prev, pageId: 0 }));
                        }
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col className="form-column">
                  <Form.Group>
                    <Form.Label>Sprint</Form.Label>
                    <Select
                      isDisabled={readOnly}
                      options={sprints.map((p) => ({
                        label: p.sprint,
                        value: p.id.toString(),
                      }))}
                      value={
                        sprint.id
                          ? {
                              label:
                                sprints.find((p) => p.id === sprint.id)
                                  ?.sprint || "",
                              value: sprint.id.toString(),
                            }
                          : null
                      }
                      placeholder="Select Sprint"
                      isMulti={false}
                      onChange={(selectedOption) => {
                        if (selectedOption) {
                          const id = parseInt(selectedOption.value, 10);
                          const name =
                            sprints.find((p) => p.id === id)?.sprint || "";
                          setSprint({ id, name });
                          setTask((prev) => ({ ...prev, sprintId: id }));
                        } else {
                          setSprint({ id: 0, name: "" });
                          setTask((prev) => ({ ...prev, sprintId: 0 }));
                        }
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col className="form-column">
                  <Form.Group>
                    <Form.Label>Priority</Form.Label>
                    <Form.Select
                      name="priority"
                      value={task.priority}
                      onChange={handleChange}
                      disabled={readOnly}
                      style={{
                        borderColor: "rgb(215, 210, 210)",
                      }}
                    >
                      <option value="">Select Priority</option>
                      <option value="High">High</option>
                      <option value="Medium">Medium</option>
                      <option value="Low">Low</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-1">
                <Col className="form-column1">
                  <Form.Group>
                    <Form.Label>Title</Form.Label>
                    <Form.Control
                      name="title"
                      value={task.title}
                      onChange={handleChange}
                      disabled={!isEditable}
                      placeholder="Title"
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-1">
                <Col className="form-column1">
                  <Form.Group>
                    <Form.Label>Description</Form.Label>
                    <RichTextEditor
                      value={task.description}
                      isEditable={isEditable}
                      onChange={(html) =>
                        handleChange({
                          target: { name: "description", value: html },
                        })
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-1">
                <Col className="form-column">
                  <Form.Group>
                    <Form.Label>Dev Est. Hours</Form.Label>
                    <Form.Control
                      type="text"
                      name="devEstHrs"
                      value={task.devEstHrs}
                      onChange={handleChange}
                      disabled={!isEditable}
                      onWheel={preventScroll}
                    />
                  </Form.Group>
                </Col>
                <Col className="form-column">
                  <Form.Group>
                    <Form.Label>Dev Act. Hours</Form.Label>
                    <Form.Control
                      type="number"
                      name="devActHrs"
                      value={task.devActHrs}
                      onChange={handleChange}
                      disabled={!isEditable}
                      onWheel={preventScroll}
                    />
                  </Form.Group>
                </Col>
                <Col className="form-column">
                  <Form.Group>
                    <Form.Label>QA Est. Hours</Form.Label>
                    <Form.Control
                      type="number"
                      name="qaEstHrs"
                      value={task.qaEstHrs}
                      onChange={handleChange}
                      disabled={!isEditable}
                      onWheel={preventScroll}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-1">
                <Col className="form-column">
                  <Form.Group>
                    <Form.Label>QA Act. Hours</Form.Label>
                    <Form.Control
                      type="number"
                      name="qaActHrs"
                      value={task.qaActHrs}
                      onChange={handleChange}
                      disabled={!isEditable}
                      onWheel={preventScroll}
                    />
                  </Form.Group>
                </Col>
                <Col className="form-column">
                  <Form.Group>
                    <Form.Label>BA Est. Hours</Form.Label>
                    <Form.Control
                      type="number"
                      name="baEstHrs"
                      value={task.baEstHrs}
                      onChange={handleChange}
                      disabled={!isEditable}
                      onWheel={preventScroll}
                    />
                  </Form.Group>
                </Col>
                <Col className="form-column">
                  <Form.Group>
                    <Form.Label>BA Act. Hours</Form.Label>
                    <Form.Control
                      type="number"
                      name="baActHrs"
                      value={task.baActHrs}
                      onChange={handleChange}
                      disabled={!isEditable}
                      onWheel={preventScroll}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-1">
                <Col className="form-column">
                  <Form.Group>
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="startDate"
                      value={task.startDate}
                      onChange={handleChange}
                      disabled={!isEditable}
                    />
                  </Form.Group>
                </Col>
                <Col className="form-column">
                  <Form.Group>
                    <Form.Label>Completion Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="completionDate"
                      value={task.completionDate || ""}
                      onChange={handleChange}
                      disabled={!isEditable}
                    />
                  </Form.Group>
                </Col>
                <Col className="form-column">
                  <Form.Group>
                    <Form.Label>Plan Release Version</Form.Label>
                    <Form.Control
                      name="planReleaseVersion"
                      value={task.planReleaseVersion}
                      onChange={handleChange}
                      disabled={!isEditable}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-1">
                <Col className="form-column">
                  <Form.Group>
                    <Form.Label>Assigned Developer</Form.Label>
                    <Select
                      required
                      isDisabled={readOnly}
                      options={employees.map((p) => ({
                        label: p.name,
                        value: p.id.toString(),
                      }))}
                      value={
                        task.assignedDev && task.assignedDev !== "0"
                          ? {
                              label: task.assignedDev,
                              value:
                                employees
                                  .find((p) => p.name === task.assignedDev)
                                  ?.id.toString() || "",
                            }
                          : null
                      }
                      placeholder="Select employee"
                      isMulti={false}
                      onChange={(selectedOption) => {
                        if (selectedOption) {
                          const id = parseInt(selectedOption.value, 10);
                          const name =
                            employees.find((p) => p.id === id)?.name || "";
                          setTask((prev) => ({ ...prev, assignedDev: name }));
                        } else {
                          setTask((prev) => ({ ...prev, assignedDev: "" }));
                        }
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col className="form-column">
                  <Form.Group>
                    <Form.Label>Assigned QA</Form.Label>
                    <Select
                      required
                      isDisabled={readOnly}
                      options={employees.map((p) => ({
                        label: p.name,
                        value: p.id.toString(),
                      }))}
                      value={
                        task.assignedQa && task.assignedQa !== "0"
                          ? {
                              label: task.assignedQa,
                              value:
                                employees
                                  .find((p) => p.name === task.assignedQa)
                                  ?.id.toString() || "",
                            }
                          : null
                      }
                      placeholder="Select employee"
                      isMulti={false}
                      onChange={(selectedOption) => {
                        if (selectedOption) {
                          const id = parseInt(selectedOption.value, 10);
                          const name =
                            employees.find((p) => p.id === id)?.name || "";
                          setTask((prev) => ({ ...prev, assignedQa: name }));
                        } else {
                          setTask((prev) => ({ ...prev, assignedQa: "" }));
                        }
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col className="form-column">
                  <Form.Group>
                    <Form.Label>Assigned BA</Form.Label>
                    <Select
                      required
                      isDisabled={readOnly}
                      options={employees.map((p) => ({
                        label: p.name,
                        value: p.id.toString(),
                      }))}
                      value={
                        task.assignedBa && task.assignedBa !== "0"
                          ? {
                              label: task.assignedBa,
                              value:
                                employees
                                  .find((p) => p.name === task.assignedBa)
                                  ?.id.toString() || "",
                            }
                          : null
                      }
                      placeholder="Select employee"
                      isMulti={false}
                      onChange={(selectedOption) => {
                        if (selectedOption) {
                          const id = parseInt(selectedOption.value, 10);
                          const name =
                            employees.find((p) => p.id === id)?.name || "";
                          setTask((prev) => ({ ...prev, assignedBa: name }));
                        } else {
                          setTask((prev) => ({ ...prev, assignedBa: "" }));
                        }
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-1">
                {initialData?.id && (
                  <Col className="form-column">
                    <Form.Group>
                      <Form.Label>Status</Form.Label>
                      <Select
                        isDisabled={readOnly}
                        options={[
                          { label: "New", value: "New" },
                          { label: "Progress", value: "Progress" },
                          { label: "Ready", value: "Ready" },
                          { label: "Complete", value: "Complete" },
                          { label: "Verify Dev", value: "Verify Dev" },
                        ]}
                        value={{ label: status, value: status }}
                        placeholder="Select Status"
                        onChange={(selectedOption) => {
                          if (selectedOption !== null) {
                            setStatus(selectedOption.value);
                            setTask((prev) => ({
                              ...prev,
                              status: selectedOption.value,
                            }));
                          }
                        }}
                        isClearable={false}
                      />
                    </Form.Group>
                  </Col>
                )}
              </Row>
            </>
          )}
          <Row className="mb-1">
            <Col md={12}>
              {renderFormContent()}
              {!readOnly && activeTab === "details" && (
                <div className="form-buttons">
                  {initialData?.id && (
                    <Button
                      className="btn-add"
                      onClick={() => {
                        setLinkedParentId(task.id);
                        setSubTask({
                          ...initialState,
                          parentTaskId: task.id,
                          projectId: task.projectId,
                          moduleId: task.moduleId,
                          subModuleId: task.subModuleId,
                          pageId: task.pageId,
                        });

                        setShowSubTaskForm(true);
                        setActiveTab("linked");
                      }}
                    >
                      Add Sub Task
                    </Button>
                  )}
                  {/* {task.parentTaskId ? (
                    <Button
                      className="btn-clear"
                      onClick={() => {
                        const parent = tasks.find(
                          (t) => t.id === task.parentTaskId,
                        );
                        if (parent) {
                          setTask(parent);
                          setActiveTab("details");
                          setIsEditable(true);
                        } else {
                          toast.error("Parent task not found");
                        }
                      }}
                    >
                      View Parent Task
                    </Button>
                  ) : (
                    <Button
                      className="btn-clear"
                      onClick={() => {
                        setActiveTab("linked");
                        setShowSubTaskForm(false);
                      }}
                    >
                      View Sub Task
                    </Button>
                  )} */}
                  {task.parentTaskId && (
                    <Button
                      className="btn-clear"
                      onClick={() => {
                        const parent = tasks.find(
                          (t) => t.id === task.parentTaskId,
                        );
                        if (parent) {
                          setTask(parent);
                          setActiveTab("details");
                          setIsEditable(true);
                        } else {
                          toast.error("Parent task not found");
                        }
                      }}
                    >
                      View Parent Task
                    </Button>
                  )}

                  {!task.parentTaskId && hasSubTasks && (
                    <Button
                      className="btn-clear"
                      onClick={() => {
                        setActiveTab("linked");
                        setShowSubTaskForm(false);
                      }}
                    >
                      View Sub Task
                    </Button>
                  )}

                  <Button type="submit" className="btn-save">
                    {initialData?.id ? "Update" : "Save"}
                  </Button>
                  <Button
                    type="button"
                    className="btn-clear"
                    onClick={handleClear}
                  >
                    Clear All
                  </Button>
                </div>
              )}
            </Col>
          </Row>
        </Form>
      </div>
    </div>
  );
};

export default TaskForm;
